import { classNames } from "helpers";
import { ButtonMenuItemProps } from "./types";
import { ReactSVG } from "react-svg";
import { useCallback } from "react";
import { Tooltip } from "ui/components/Tooltip";

const ButtonMenuItem: React.FC<ButtonMenuItemProps> = ({
  children,
  className,
  startIcon,
  endIcon,
  size,
  variant,
  disabled,
  tooltip,
  onClick,
}) => {
  const handleItemClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (disabled) return;
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled]
  );

  const sizeClassName = () => {
    switch (size) {
      case "small":
        return "px-2 py-1.5 text-sm";
      case "medium":
        return "px-3 py-2";
      case "large":
        return "px-3 py-3";
      case "full":
        return "w-full px-3 py-3";
      default:
        return "px-3 py-3";
    }
  };

  const variantClassName = () => {
    if (disabled) return "text-slate-400";
    switch (variant) {
      case "text":
        return "text-blue-500";
      case "outlined":
        return "ring-1 ring-gray-300 hover:bg-blue-500 hover:ring-0 hover:text-white transition ease-in-out delay-150";
      case "primary":
        return "bg-blue-500 text-white hover:bg-blue-900 hover:text-white transition ease-in-out delay-150";
      case "secondary":
        return "bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition ease-in-out delay-150";
      case "warning":
        return "bg-yellow-100 text-yellow-700 hover:bg-yellow-500 hover:text-white transition ease-in-out delay-150";
      case "remove":
        return "text-red-600 hover:text-red-700 transition ease-in-out delay-150";
      default:
        return "text-slate-600 hover:bg-blue-100";
    }
  };

  const iconClassName = () => {
    if (disabled) return "stroke-slate-300";
    switch (variant) {
      case "text":
        return "stroke-slate-600";
      case "outlined":
        return "stroke-[#181b31]";
      case "primary":
        return "stroke-[#fff]";
      case "secondary":
        return "stroke-blue-300";
      case "warning":
        return "stroke-yellow-600 group-hover:stroke-white";
      case "remove":
        return "stroke-red-600";
      default:
        return "stroke-gray-500";
    }
  };

  const iconSize = () => {
    switch (size) {
      case "small":
        return "w-4 h-4";
      default:
        return "w-5 h-5";
    }
  };

  const item = (
    <div
      onClick={handleItemClick}
      className={classNames(
        "group",
        sizeClassName(),
        variantClassName(),
        className ?? "",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        "relative flex w-full items-center text-sm font-medium rounded-sm"
      )}
    >
      {startIcon && (
        <div className="flex items-center justify-center ">
          <ReactSVG
            src={startIcon}
            className={classNames(
              iconClassName(),
              iconSize(),
              "transition ease-in-out delay-150"
            )}
          />
        </div>
      )}
      {children && <span className="ml-2 mr-2 inline-block group-hover:text-inherit">{children}</span>}
      {endIcon && (
        <div className="flex items-center justify-center mr-2">
          <ReactSVG
            src={endIcon}
            className={classNames(
              iconClassName(),
              iconSize(),
              "transition ease-in-out delay-150"
            )}
          />
        </div>
      )}
    </div>
  );

  // Onemogućena stavka objašnjava zašto se ne može odabrati.
  if (disabled && tooltip) {
    return (
      <Tooltip
        trigger={item}
        description={tooltip}
        variant="dark"
        position="top-center"
      />
    );
  }

  return item;
};

export default ButtonMenuItem;
